import { common } from 'themes/default';
import { CLASSES_OF_SERVICE_BY_TYPE, WC_COMP, EVAC, UNASSIGNED } from 'constants/classOfService';
import {
  getGroupedFaresByClassOfService,
  getSummatedFarePriorityList
} from '../JourneySearchTables/SearchResultsTable/SearchResultsTable.utils';

import { brandCode } from 'constants/brandCodes';

export const buildPaxInfosPerFareName = (infos, legs) => {
  const paxInfoArray = Object.values(infos);

  return paxInfoArray.reduce((obj, item) => {
    if (!obj[item.name]) {
      obj[item.name] = [];
    }

    obj[item.name].push(item);

    for (const leg of legs) {
      const { classOfService, products, serviceType } = leg;

      if (products.some(({ tariffCode }) => tariffCode === item.tariffCode)) {
        item.classOfServiceCode = classOfService.code;
        item.serviceTypeCode = serviceType?.code;
        break;
      }
    }

    return obj;
  }, {});
};

export const buildSelectionTabs = (classesOfService = [], isWheelchairSearch = false, isEvacSearch = false, isUnassignedSearch = false) => {
  const colors = common.searchResults.header;
  const tabs = [];

  let fareClasses = [];

  if (isWheelchairSearch) {
    fareClasses = classesOfService.filter(({ code }) => CLASSES_OF_SERVICE_BY_TYPE[WC_COMP].includes(code));
  } else if (isEvacSearch) {
    fareClasses = classesOfService.filter(({ code }) => CLASSES_OF_SERVICE_BY_TYPE[EVAC].includes(code));
  } else if (isUnassignedSearch) {
    fareClasses = classesOfService.filter(({ code }) => CLASSES_OF_SERVICE_BY_TYPE[UNASSIGNED].includes(code));
  } else {
    fareClasses = classesOfService.filter(
      ({ code }) =>
        !CLASSES_OF_SERVICE_BY_TYPE[WC_COMP].includes(code) &&
        !CLASSES_OF_SERVICE_BY_TYPE[EVAC].includes(code) &&
        !CLASSES_OF_SERVICE_BY_TYPE[UNASSIGNED].includes(code)
    );
  }

  const classTabs = fareClasses.map(({ code, name }) => {
    const classColor = colors[code] ?? colors.default;

    return {
      key: code,
      text: name,
      backgroundColor: classColor.backgroundColor ?? classColor.backgroundColour
    };
  });

  tabs.push(...classTabs);

  return tabs;
};

export const buildFares = (selectedJourney, passengers) =>
  selectedJourney?.fares
    ?.map((fare) => {
      const products = fare?.legs.map(({ products: legProducts }) => legProducts?.filter(({ isFareAddOn }) => !isFareAddOn)).flat() ?? [];

      if (products.length === 0) {
        return null;
      }

      const totalPrice = Number(products.reduce((total, product) => total + product?.price ?? 0, 0).toFixed(2));
      const totalLoyaltyPoints = products.reduce((total, product) => total + product?.loyaltyPoints ?? 0, 0);
      const name = products?.[0]?.name ?? '';

      const paxInfo =
        products?.reduce((obj, { tariffCode, price, loyaltyPoints, passengerId }) => {
          const passenger = passengers?.find(({ id }) => id === passengerId);
          const id = `${tariffCode}-${passenger?.type}`;
          const summary = obj[id];

          let fareAddOnPrice = 0;

          fare.legs.forEach(({ products: legProducts }) =>
            legProducts
              .filter(({ isFareAddOn }) => isFareAddOn)
              .some(({ passengerId: addOnPassengerId, price: addOnPrice }) => {
                if (addOnPassengerId === passengerId) {
                  fareAddOnPrice = addOnPrice;

                  return true;
                }

                return false;
              })
          );

          return {
            ...obj,
            [id]: {
              disabilityType: passenger?.disabilityType ?? '',
              type: passenger?.type ?? '',
              count: summary !== undefined ? summary.count + 1 : 1,
              price: totalPrice === 0 ? 0 : price + fareAddOnPrice,
              tariffCode,
              name: products.find((p) => p.tariffCode === tariffCode)?.name,
              id,
              loyaltyPoints
            }
          };
        }, {}) ?? {};

      return {
        ...fare,
        name,
        paxInfo,
        totalPrice,
        totalLoyaltyPoints
      };
    })
    .filter(Boolean) ?? [];

export const buildFareName = (name = '', paxInfo = []) => `${name}_${Object.keys(paxInfo).join('-')}`;

export const isSummatedFare = (legs, productCodes) => legs.length > 1 && !productCodes.every((code) => code === productCodes[0]);

export const isThroughFare = (legs, productCodes) => legs.length > 1 && productCodes.every((code) => code === productCodes[0]);

export const getCurrentFares = ({ currentClass, fareClasses = [], exchangedClassesOfService }) => {
  const filteredFareClasses = fareClasses.filter((f) => f?.classOfService?.code === currentClass);

  const isLyriaSummatedFare =
    filteredFareClasses.length > 0 && filteredFareClasses.every(({ isLyria, isSummatedFare }) => isLyria && isSummatedFare);

  const isSNCFSummatedFare =
    filteredFareClasses.length > 0 &&
    filteredFareClasses.every(
      ({ legs, isSummatedFare }) => legs.some(({ serviceType }) => serviceType.brandCode === brandCode.SNCF) && isSummatedFare
    );

  const isINOUISummatedFare =
    filteredFareClasses.length > 0 &&
    filteredFareClasses.every(
      ({ legs, isSummatedFare }) => legs.some(({ serviceType }) => serviceType.brandCode === brandCode.INOUI) && isSummatedFare
    );

  if (isLyriaSummatedFare) {
    const lyriaPriorityList = getSummatedFarePriorityList({ currentClass, exchangedClassesOfService, isLyria: true });

    const groupedFaresByLyriaClassOfService = getGroupedFaresByClassOfService({ fares: filteredFareClasses, isLyria: true });

    return lyriaPriorityList.flatMap((classOfService) => groupedFaresByLyriaClassOfService[classOfService] || []);
  }

  if (isSNCFSummatedFare || isINOUISummatedFare) {
    const SNCFPriorityList = getSummatedFarePriorityList({
      currentClass,
      exchangedClassesOfService,
      isSNCF: isSNCFSummatedFare,
      isInoui: isINOUISummatedFare
    });

    const groupedFaresBySNCFClassOfService = getGroupedFaresByClassOfService({
      fares: filteredFareClasses,
      isSNCF: isSNCFSummatedFare,
      isInoui: isINOUISummatedFare
    });

    return SNCFPriorityList.flatMap((classOfService) => groupedFaresBySNCFClassOfService[classOfService] || []);
  }

  return filteredFareClasses;
};

export const getInitialFare = ({ currentFareClasses = [], currentFareInfo = {} } = {}) =>
  currentFareClasses.find(
    ({ name, paxInfo }) => buildFareName(name, paxInfo) === buildFareName(currentFareInfo?.name, currentFareInfo?.paxInfo)
  ) || currentFareClasses[0];
